import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import PaymentMethodModal from "../../../components/common/PaymentMethodModal";
import {
  ConfirmationMethodProps,
  ConfirmationMethodResult,
  SelectedPaymentType,
  SnackBarStates,
} from "../../../components/common/PaymentMethodModal/types";
import {
  CashOutAmount,
  ESenderRole,
  UserProfile,
} from "../../../setup/api/services/contract/models";
import { ContractContext } from "../../../providers/contract";
import {
  checkCashoutPayment,
  makeCashOutPayment,
  makeManualCashOutPayment,
  makeOtherContractCashOutPayment,
  manualRefundTransaction,
  refundTransaction,
} from "../../../setup/api/services/payment";
import { ICashoutRequest } from "../../../setup/api/services/payment/models";
import { IUserInfoResponseData } from "../../../setup/api/services/user/models";
import {
  ACTION_UNDER_PROCESSING,
  CANNOT_MAKE_THIS_CALL,
} from "../../contract/constants";

interface INewCashoutModal {
  userType: ESenderRole;
  cashOutAmount: CashOutAmount;
  open: boolean;
  onClose: () => void;
  user: IUserInfoResponseData | Partial<UserProfile> | undefined;
}

const NewCashoutModal = ({
  userType,
  cashOutAmount,
  open,
  onClose,
  user,
}: INewCashoutModal) => {
  const { contract } = useContext(ContractContext);

  const [amount, setAmount] = useState<number | string>();
  const [gatewayPaymentId, setGatewayPaymentId] = useState<string>();
  const [targetContractDomainId, setTargetContractDomainId] = useState<
    string | undefined
  >();

  const isTargetContractLocked = useMemo(
    () => !!cashOutAmount.settlementRequestId,
    [cashOutAmount.settlementRequestId]
  );

  useEffect(() => {
    if (!cashOutAmount.settlementRequestId || !contract?.settlementRequests)
      return;
    const match = contract.settlementRequests.find(
      (sr) => sr.id === cashOutAmount.settlementRequestId
    );
    if (match) setTargetContractDomainId(match.destinationContractShortId);
  }, [cashOutAmount.settlementRequestId, contract?.settlementRequests, open]);
  const [transactionDate, setTransactionDate] = useState<Date | null>(
    new Date()
  );

  const checkCashoutStatus = useCallback(async () => {
    try {
      await checkCashoutPayment(
        contract?.transactions
          ? contract?.transactions[0]?.referenceTransactionId
          : "",
        String(user?.userIdentification)
      );
      return;
    } catch (err) {
      console.error(err);
    }
  }, [contract, user]);

  const handleSingleViewCashout =
    useCallback(async (): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      const shouldClose = false;
      let success = false;

      if (!contract || !user) {
        return { shouldClose: false, success: false };
      }

      try {
        const cashoutBody: ICashoutRequest = {
          amount: Number(amount) || 0,
          referenceTransactionId: contract?.transactions
            ? contract?.transactions[0]?.referenceTransactionId
            : "",
          assignedBeneficiaryId: String(user.userIdentification),
          assignedBeneficiaryName: user.profile?.fullName || "",
        };

        const result = await makeCashOutPayment(cashoutBody);
        const isSuccess = result?.data?.status !== "FAILED";

        if (isSuccess) {
          snackBarStates = {
            title: result?.data?.statusDescription || ACTION_UNDER_PROCESSING,
            color: "success",
          };
          await checkCashoutStatus();
          success = true;
        } else {
          snackBarStates = {
            title: result?.data?.statusDescription || ACTION_UNDER_PROCESSING,
            color: "error",
          };
        }
      } catch (error: unknown) {
        snackBarStates = {
          title: CANNOT_MAKE_THIS_CALL,
          color: "error",
        };
        success = false;
      }

      return { snackBarStates, shouldClose, success };
    }, [contract, user, amount, checkCashoutStatus]);

  const handleManualCashout =
    useCallback(async (): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      const shouldClose = false;
      let success = false;

      if (!contract || !user) {
        return { shouldClose: false, success: false };
      }

      try {
        const cashoutBody: ICashoutRequest = {
          amount: cashOutAmount.cashOutAmount,
          referenceTransactionId: contract?.transactions
            ? contract?.transactions[0]?.referenceTransactionId
            : "",
          assignedBeneficiaryId: String(user.userIdentification),
          assignedBeneficiaryName: user.profile?.fullName || "",
          gatewayPaymentId: String(gatewayPaymentId),
          transactionDate: transactionDate
            ? transactionDate.toISOString()
            : new Date().toISOString(),
        };

        const result = await makeManualCashOutPayment(cashoutBody);
        const isSuccess = result?.data?.status !== "FAILED";

        if (isSuccess) {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "success",
          };
          success = true;
        } else {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "error",
          };
        }
      } catch (error: unknown) {
        snackBarStates = {
          title: CANNOT_MAKE_THIS_CALL,
          color: "error",
        };
        success = false;
      }

      return { snackBarStates, shouldClose, success };
    }, [
      contract,
      user,
      gatewayPaymentId,
      transactionDate,
      cashOutAmount.cashOutAmount,
    ]);

  const handleTabbyRefund = useCallback(
    async (isManual: boolean): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      const shouldClose = false;
      let success = false;

      if (!contract || !user) {
        return { shouldClose: false, success: false };
      }

      try {
        const cashoutBody: ICashoutRequest = {
          amount: isManual ? cashOutAmount.cashOutAmount : Number(amount) || 0,
          referenceTransactionId: contract?.transactions
            ? contract?.transactions[0]?.referenceTransactionId
            : "",
          domainId: contract.shortId,
          ...(isManual && { gatewayPaymentId: String(gatewayPaymentId) }),
        };

        const result = isManual
          ? await manualRefundTransaction(cashoutBody)
          : await refundTransaction(cashoutBody);

        const isSuccess = result?.data?.status !== "FAILED";

        if (isSuccess) {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "success",
          };
          success = true;
        } else {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "error",
          };
        }
      } catch (error: unknown) {
        snackBarStates = {
          title: CANNOT_MAKE_THIS_CALL,
          color: "error",
        };
        success = false;
      }

      return { snackBarStates, shouldClose, success };
    },
    [contract, user, amount, gatewayPaymentId, cashOutAmount.cashOutAmount]
  );

  const handleMoyasarRefund = useCallback(
    async (isManual: boolean): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      const shouldClose = false;
      let success = false;

      if (!contract || !user) {
        return { shouldClose: false, success: false };
      }

      try {
        const cashoutBody: ICashoutRequest = {
          amount: isManual ? cashOutAmount.cashOutAmount : Number(amount) || 0,
          referenceTransactionId: contract?.transactions
            ? contract?.transactions[0]?.referenceTransactionId
            : "",
          domainId: contract.shortId,
          ...(isManual && { gatewayPaymentId: String(gatewayPaymentId) }),
        };

        const result = isManual
          ? await manualRefundTransaction(cashoutBody)
          : await refundTransaction(cashoutBody);

        const isSuccess = result?.data?.status !== "FAILED";

        if (isSuccess) {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "success",
          };
          success = true;
        } else {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "error",
          };
        }
      } catch (error: unknown) {
        snackBarStates = {
          title: CANNOT_MAKE_THIS_CALL,
          color: "error",
        };
        success = false;
      }

      return { snackBarStates, shouldClose, success };
    },
    [contract, user, amount, gatewayPaymentId, cashOutAmount.cashOutAmount]
  );

  const handleNeoRefund = useCallback(
    async (isManual: boolean): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      const shouldClose = false;
      let success = false;

      if (!contract || !user) {
        return { shouldClose: false, success: false };
      }

      try {
        const cashoutBody: ICashoutRequest = {
          amount: isManual ? cashOutAmount.cashOutAmount : Number(amount) || 0,
          referenceTransactionId: contract?.transactions
            ? contract?.transactions[0]?.referenceTransactionId
            : "",
          domainId: contract.shortId,
          ...(isManual && { gatewayPaymentId: String(gatewayPaymentId) }),
        };

        const result = isManual
          ? await manualRefundTransaction(cashoutBody)
          : await refundTransaction(cashoutBody);

        const isSuccess = result?.data?.status !== "FAILED";

        if (isSuccess) {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "success",
          };
          success = true;
        } else {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "error",
          };
        }
      } catch (error: unknown) {
        snackBarStates = {
          title: CANNOT_MAKE_THIS_CALL,
          color: "error",
        };
        success = false;
      }

      return { snackBarStates, shouldClose, success };
    },
    [contract, user, amount, gatewayPaymentId, cashOutAmount.cashOutAmount]
  );

  const handleTamaraRefund = useCallback(
    async (isManual: boolean): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      const shouldClose = false;
      let success = false;

      if (!contract || !user) {
        return { shouldClose: false, success: false };
      }

      try {
        const cashoutBody: ICashoutRequest = {
          amount: isManual ? cashOutAmount.cashOutAmount : Number(amount) || 0,
          referenceTransactionId: contract?.transactions
            ? contract?.transactions[0]?.referenceTransactionId
            : "",
          domainId: contract.shortId,
          ...(isManual && { gatewayPaymentId: String(gatewayPaymentId) }),
        };

        const result = isManual
          ? await manualRefundTransaction(cashoutBody)
          : await refundTransaction(cashoutBody);

        const isSuccess = result?.data?.status !== "FAILED";

        if (isSuccess) {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "success",
          };
          success = true;
        } else {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "error",
          };
        }
      } catch (error: unknown) {
        snackBarStates = {
          title: CANNOT_MAKE_THIS_CALL,
          color: "error",
        };
        success = false;
      }

      return { snackBarStates, shouldClose, success };
    },
    [contract, user, amount, gatewayPaymentId, cashOutAmount.cashOutAmount]
  );

  const handleOtherContractCashout =
    useCallback(async (): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      const shouldClose = false;
      let success = false;

      if (!contract || !user) {
        return { shouldClose: false, success: false };
      }

      try {
        const cashoutBody: ICashoutRequest = {
          amount: cashOutAmount.cashOutAmount,
          referenceTransactionId: contract?.transactions
            ? contract?.transactions[0]?.referenceTransactionId
            : "",
          assignedBeneficiaryId: String(user.userIdentification),
          assignedBeneficiaryName: user.profile?.fullName || "",
          domainId: contract.shortId,
          transactionDate: new Date().toISOString(),
          targetContractDomainId: String(targetContractDomainId),
        };

        const result = await makeOtherContractCashOutPayment(cashoutBody);
        const isSuccess = result?.data?.status !== "FAILED";

        if (isSuccess) {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "success",
          };
          success = true;
        } else {
          snackBarStates = {
            title: ACTION_UNDER_PROCESSING,
            color: "error",
          };
        }
      } catch (error: unknown) {
        snackBarStates = {
          title: CANNOT_MAKE_THIS_CALL,
          color: "error",
        };
        success = false;
      }

      return { snackBarStates, shouldClose, success };
    }, [contract, user, cashOutAmount.cashOutAmount, targetContractDomainId]);

  const onConfirm = useCallback(
    async ({
      selectionType,
      isManualRefund = false,
    }: ConfirmationMethodProps): Promise<ConfirmationMethodResult> => {
      if (!contract || !user) {
        return { shouldClose: false, success: false };
      }

      // Handle different payment methods
      if (selectionType === SelectedPaymentType.SINGLE_VIEW) {
        return await handleSingleViewCashout();
      }

      if (selectionType === SelectedPaymentType.BANK) {
        return await handleManualCashout();
      }

      if (
        selectionType === SelectedPaymentType.TABBY &&
        userType === ESenderRole.CUSTOMER
      ) {
        return await handleTabbyRefund(isManualRefund);
      }

      if (
        selectionType === SelectedPaymentType.MOYASAR &&
        userType === ESenderRole.CUSTOMER
      ) {
        return await handleMoyasarRefund(isManualRefund);
      }

      if (
        selectionType === SelectedPaymentType.NEO &&
        userType === ESenderRole.CUSTOMER
      ) {
        return await handleNeoRefund(isManualRefund);
      }

      if (
        selectionType === SelectedPaymentType.TAMARA &&
        userType === ESenderRole.CUSTOMER
      ) {
        return await handleTamaraRefund(isManualRefund);
      }

      if (selectionType === SelectedPaymentType.OTHER_CONTRACT) {
        return await handleOtherContractCashout();
      }

      return { shouldClose: false, success: false };
    },
    [
      contract,
      user,
      userType,
      handleSingleViewCashout,
      handleManualCashout,
      handleTabbyRefund,
      handleMoyasarRefund,
      handleNeoRefund,
      handleTamaraRefund,
      handleOtherContractCashout,
    ]
  );

  return (
    <PaymentMethodModal
      open={open}
      onClose={onClose}
      contract={contract}
      userType={userType}
      userId={user?.userIdentification}
      amount={amount}
      setAmount={setAmount}
      gatewayPaymentId={gatewayPaymentId}
      setGatewayPaymentId={setGatewayPaymentId}
      targetContractDomainId={targetContractDomainId}
      setTargetContractDomainId={setTargetContractDomainId}
      isTargetContractLocked={isTargetContractLocked}
      showOtherContractOption
      onConfirm={onConfirm}
      paymentAmount={cashOutAmount.cashOutAmount}
      transactionDate={transactionDate}
      setTransactionDate={setTransactionDate}
    />
  );
};

export default NewCashoutModal;
